import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const desktopUserMenuRef = useRef(null);
  const mobileUserMenuRef = useRef(null);

  // Base navigation pages
  const basePages = [
    { name: 'Courses', path: '/courses' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  // Conditionally add "My Courses" if user has enrolled courses
  const pages = isAuthenticated && user?.enrolled_courses && user.enrolled_courses.length > 0
    ? [...basePages.slice(0, 3), { name: 'My Courses', path: '/my-courses' }]
    : basePages;

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Admin-specific menu items
  const adminSettings = [
    { name: 'Course Management', action: () => navigate('/admin/courses'), adminOnly: true },
    { name: 'Manage Users', action: () => navigate('/admin/users'), adminOnly: true },
  ];

  const settings = [
    { name: 'Profile', action: () => navigate('/profile') },
    { name: 'Account Settings', action: () => navigate('/account-settings') },
    { name: 'Logout', action: handleLogout }
  ];

  // Combine admin settings with regular settings for admin users
  const menuSettings = user?.role === 'admin' ? [...adminSettings, ...settings] : settings;

  function handleLogout() {
    logout();
    setUserMenuOpen(false);
  }

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Check both desktop and mobile refs
      const isOutsideDesktop = desktopUserMenuRef.current && !desktopUserMenuRef.current.contains(event.target);
      const isOutsideMobile = mobileUserMenuRef.current && !mobileUserMenuRef.current.contains(event.target);

      // Close if clicked outside both (only one will be visible at a time)
      if (isOutsideDesktop && isOutsideMobile) {
        setUserMenuOpen(false);
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside); // Add touch support for mobile
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [userMenuOpen]);

  return (
    <header className="bg-blue-600 shadow-md">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo - Desktop */}
          <div className="hidden md:flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/Logo_png_file-removebg-preview.png" alt="LearnersEdge Logo" className="h-12 w-auto my-0" />
            <h2 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-orange-500 bg-clip-text text-transparent tracking-wider">LEARNERS EDGE</h2>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Logo - Mobile */}
          <div className="flex md:hidden items-center gap-2 flex-1 justify-center cursor-pointer" onClick={() => navigate('/')}>
            <img src="/Logo_png_file-removebg-preview.png" alt="LearnersEdge Logo" className="h-12 w-auto my-0" />
            <h2 className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-orange-500 bg-clip-text text-transparent tracking-wider">LEARNERS EDGE</h2>
          </div>

          {/* Navigation - Desktop */}
          <nav className="hidden md:flex flex-1 ml-10 space-x-1">
            {pages.map((page) => (
              <Link key={page.name} to={page.path}>
                <button className={`px-4 py-2 text-white rounded transition-colors font-medium ${
                  isActive(page.path)
                    ? 'bg-blue-700'
                    : 'hover:bg-blue-700'
                }`}>
                  {page.name}
                </button>
              </Link>
            ))}
          </nav>

          {/* Auth Section - Desktop */}
          {isAuthenticated ? (
            <div className="hidden md:flex items-center relative" ref={desktopUserMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 focus:outline-none"
              >
                <div className="h-10 w-10 rounded-full bg-blue-800 flex items-center justify-center text-white font-semibold border-2 border-white">
                  {user?.full_name ? user.full_name.charAt(0).toUpperCase() : user?.username?.charAt(0).toUpperCase()}
                </div>
              </button>

              {/* User Dropdown Menu */}
              {userMenuOpen && (
                <div className="absolute right-0 top-14 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.username}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    {user?.role === 'admin' && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded">
                        ADMIN
                      </span>
                    )}
                  </div>
                  {menuSettings.map((setting, index) => (
                    <div key={setting.name}>
                      {setting.adminOnly && index === 0 && (
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50">
                          Admin Options
                        </div>
                      )}
                      {!setting.adminOnly && user?.role === 'admin' && adminSettings.length > 0 && index === adminSettings.length && (
                        <div className="border-t border-gray-200 my-1"></div>
                      )}
                      <button
                        onClick={() => {
                          setting.action();
                          setUserMenuOpen(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          setting.adminOnly ? 'text-green-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {setting.name}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-3">
              <Link to="/login">
                <button className="px-5 py-2 text-white font-medium hover:bg-blue-700 rounded transition-colors">
                  Login
                </button>
              </Link>
              <Link to="/register">
                <button className="px-5 py-2 bg-white text-blue-600 font-medium rounded hover:bg-gray-100 transition-colors">
                  Sign Up
                </button>
              </Link>
            </div>
          )}

          {/* Auth Section - Mobile */}
          {isAuthenticated ? (
            <div className="flex md:hidden items-center relative" ref={mobileUserMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center focus:outline-none"
              >
                <div className="h-8 w-8 rounded-full bg-blue-800 flex items-center justify-center text-white font-semibold text-sm border-2 border-white">
                  {user?.full_name ? user.full_name.charAt(0).toUpperCase() : user?.username?.charAt(0).toUpperCase()}
                </div>
              </button>

              {/* User Dropdown Menu - Mobile */}
              {userMenuOpen && (
                <div className="fixed right-2 top-16 w-[calc(100vw-2rem)] max-w-xs bg-white rounded-md shadow-lg py-1 z-[9999] pointer-events-auto">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.username}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    {user?.role === 'admin' && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded">
                        ADMIN
                      </span>
                    )}
                  </div>
                  {menuSettings.map((setting, index) => (
                    <div key={setting.name}>
                      {setting.adminOnly && index === 0 && (
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50">
                          Admin Options
                        </div>
                      )}
                      {!setting.adminOnly && user?.role === 'admin' && adminSettings.length > 0 && index === adminSettings.length && (
                        <div className="border-t border-gray-200 my-1"></div>
                      )}
                      <button
                        onClick={() => {
                          setting.action();
                          setUserMenuOpen(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          setting.adminOnly ? 'text-green-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {setting.name}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex md:hidden">
              <Link to="/login">
                <button className="text-white font-medium text-sm">
                  Login
                </button>
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-3 pt-2 border-t border-blue-700">
            {pages.map((page) => (
              <Link key={page.name} to={page.path}>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block w-full text-left px-4 py-2 text-white rounded transition-colors ${
                    isActive(page.path)
                      ? 'bg-blue-700'
                      : 'hover:bg-blue-700'
                  }`}
                >
                  {page.name}
                </button>
              </Link>
            ))}
            {!isAuthenticated && (
              <Link to="/register">
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-left px-4 py-2 mt-2 text-white bg-blue-700 hover:bg-blue-800 rounded transition-colors"
                >
                  Sign Up
                </button>
              </Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
